<!-- =========================================================================================
  File Name: ECommerceShop.vue
  Description: eCommerce Shop Page
  ----------------------------------------------------------------------------------------

========================================================================================== -->

<template>
<div>
    <!-- AIS CONFIG -->
    <div class="algolia-header mb-4">
        <div class="flex md:items-end items-center justify-between flex-wrap">
            <!-- TOGGLE SIDEBAR BUTTON -->
            <feather-icon class="inline-flex lg:hidden cursor-pointer mr-4" icon="MenuIcon" @click.stop="toggleFilterSidebar" />

            <p class="lg:inline-flex hidden font-semibold algolia-filters-label">Filters : </p>

            <div class="flex justify-between items-end flex-grow">
                <!-- Stats -->
                <div class="font-semibold md:block hidden">{{itemsList.length}} resultat trouvés.</div>
                <div class="flex flex-wrap">
                    <!-- ITEM VIEW - GRID/LIST -->
                    <div>
                        <feather-icon icon="GridIcon" @click="currentItemView='item-grid-view'" class="p-2 shadow-drop rounded-lg d-theme-dark-bg cursor-pointer" :svgClasses="{'text-primary stroke-current': currentItemView == 'item-grid-view'}" />
                        <feather-icon icon="ListIcon" @click="currentItemView='item-list-view'" class="p-2 ml-4 shadow-drop rounded-lg d-theme-dark-bg cursor-pointer hidden sm:inline-flex" :svgClasses="{'text-primary stroke-current': currentItemView == 'item-list-view'}" />
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div id="algolia-content-container" class="relative clearfix">

        <vs-sidebar class="items-no-padding vs-sidebar-rounded background-absolute" :hidden-background="clickNotClose" v-model="isFilterSidebarActive">

            <div class="p-6 filter-container">
                <!-- CATEGORIES -->
                <h6 class="font-bold mb-4">CATEGORIES</h6>
                <div>
                    <ul>
                        <li v-for="(item, indextr) in productsCategories" :key="indextr" class="flex items-center cursor-pointer py-1">
                            <vs-radio v-model="categorie" :vs-value="item.value">{{ item.label }}</vs-radio>
                        </li>
                    </ul>
                </div>

                <!-- PRICE SLIDER -->
                <h6 style="margin-top:40px" class="font-bold mb-4">Prix</h6>
                <div>
                    <vs-slider style="min-width: 1px;margin-top:40px;margin-bottom:40px" text-fixed="DT" :min="range.min" :max="range.max" color="primary" v-model="prix" />
                </div>

                <div class="flex justify-center">
                    <vs-button class="w-full" @click.prevent="rechercher" :disabled="!canRefine">
                        Remove
                        Filters
                    </vs-button>
                </div>
            </div>

        </vs-sidebar>

        <!-- RIGHT COL -->
        <div :class="{'sidebar-spacer-with-margin': clickNotClose}">
            <!-- SEARCH BAR -->
            <div>
                <div class="relative mb-8">
                    <!-- SEARCH INPUT -->
                    <vs-input class="w-full vs-input-shadow-drop vs-input-no-border d-theme-input-dark-bg" placeholder="Chercher ici .." v-model="searchQuery" @input="rechercher($event)" size="large" />

                    <!-- SEARCH LOADING -->
                    <p :hidden="!isSearchStalled" class="mt-4 text-grey">
                        <feather-icon icon="ClockIcon" svgClasses="w-4 h-4" class="mr-2 align-middle" />
                        <span>Loading...</span>
                    </p>

                    <!-- SEARCH ICON -->
                    <div slot="submit-icon" class="absolute top-0 right-0 py-4 px-6" v-show="!searchQuery">
                        <feather-icon icon="SearchIcon" svgClasses="h-6 w-6" />
                    </div>

                    <!-- CLEAR INPUT ICON -->
                    <div slot="reset-icon" class="absolute top-0 right-0 py-4 px-6" v-show="searchQuery">
                        <feather-icon icon="XIcon" svgClasses="h-6 w-6 cursor-pointer" @click="rechercher('')" />
                    </div>
                </div>
            </div>

            <!-- SEARCH RESULT -->
            <div>
                <div>
                    <!-- GRID VIEW -->
                    <template v-if="currentItemView == 'item-grid-view'">
                        <div class="items-grid-view vx-row match-height">
                            <div class="vx-col lg:w-1/4 sm:w-1/2 w-full" v-for="(item, indextr) in itemsList" :key="indextr">
                                <item-grid-view :item="item" :isTheLast="itemsList.length == (indextr+1) ? true : false">
                                    <!-- SLOT: ACTION BUTTONS -->
                                    <template slot="action-buttons">
                                        <div class="flex flex-wrap">
                                            <!-- PRIMARY BUTTON: ADD TO WISH LIST -->
                                            <div class="item-view-primary-action-btn p-3 flex flex-grow items-center justify-center cursor-pointer" @click="toggleItemInWishList(item)">
                                                <feather-icon icon="HeartIcon" :svgClasses="[{'text-danger fill-current' : isInWishList(item.code)}, 'h-4 w-4']" />

                                                <span class="text-sm font-semibold ml-2">Favorie</span>
                                            </div>

                                            <!-- SECONDARY BUTTON: ADD-TO/VIEW-IN CART -->
                                            <div class="item-view-secondary-action-btn bg-primary p-3 flex flex-grow items-center justify-center text-white cursor-pointer" @click="cartButtonClicked(item)">
                                                <feather-icon icon="ShoppingBagIcon" svgClasses="h-4 w-4" />

                                                <span class="text-sm font-semibold ml-2" @click="changeShopCart('voir',item.computed)" v-if="isInCart(item.objectID)">Voir dans panier</span>
                                                <span class="text-sm font-semibold ml-2" @click="changeShopCart('add',item.code,item.designation)" v-else>Ajout au panier</span>
                                            </div>
                                        </div>
                                    </template>
                                </item-grid-view>
                            </div>
                        </div>
                    </template>

                    <!-- LIST VIEW -->
                    <template v-else>
                        <div class="items-list-view mb-base" v-for="item in itemsList" :key="item.code">
                            <item-list-view :item="item">
                                <!-- SLOT: ACTION BUTTONS -->
                                <template slot="action-buttons">
                                    <div class="item-view-primary-action-btn p-3 rounded-lg flex flex-grow items-center justify-center cursor-pointer mb-3" @click="toggleItemInWishList(item)">
                                        <feather-icon icon="HeartIcon" :svgClasses="[{'text-danger fill-current' : isInWishList(item.code)}, 'h-4 w-4']" />
                                        <span class="text-sm font-semibold ml-2">WISHLIST</span>
                                    </div>
                                    <div class="item-view-secondary-action-btn bg-primary p-3 rounded-lg flex flex-grow items-center justify-center text-white cursor-pointer" @click="cartButtonClicked(item)">
                                        <feather-icon icon="ShoppingBagIcon" svgClasses="h-4 w-4" />

                                        <span class="text-sm font-semibold ml-2" v-if="isInCart(item.objectID)">Voir dans panier</span>
                                        <span class="text-sm font-semibold ml-2" v-else>Ajout au panier</span>
                                    </div>
                                </template>
                            </item-list-view>
                        </div>
                    </template>
                </div>
            </div>

            <!-- PAGINATION -->
            <div>
                <vs-pagination :total="nbPages" :max="7" v-model="pageActuel" :key="pageActuel" @input="(val) => { paginate(val - 1) }" />
            </div>

            <!-- ALGOLIA LOGO -->
        </div>
    </div>
</div>
</template>

<script>
import indexPanier from "../../http/requests/panier/index.js";

export default {
    components: {
        ItemGridView: () => import("./components/ItemGridView.vue"),
        ItemListView: () => import("./components/ItemListView.vue")
    },
    data: function () {
        return {
            prix: [0, 1000],
            itemsData: [],
            itemsList: [],
            canRefine: true,
            isSearchStalled: false,
            default_image: require("./defualt_im.png"),
            NumberItemsPerPage: 9,
            searchQuery: "",
            pageActuel: 1,
            nbPages: 0,
            productsCategories: [{
                    value: "All",
                    label: "All"
                },
                {
                    value: "PARA",
                    label: "Parapharmacie"
                },
                {
                    value: "PHARMA",
                    label: "Médicament"
                }
            ],
            categorie: "All",
            ratingItems: [],
            itemsBrands: [],
            fournisseurs: [],
            // Filter Sidebar
            isFilterSidebarActive: true,
            clickNotClose: true,
            currentItemView: "item-grid-view",
            sortingType: "", // pour itemspriceselect
            priceIntervale: {
                start: 0,
                end: -1
            },
            sliderPriceValue: {
                min: 0,
                max: 100
            },
            range: {
                min: 0,
                max: 1000
            },
            isFromSearch: true
        };
    },
    computed: {
        toValue() {
            return (value, range) => [
                value.min !== null ? value.min : range.min,
                value.max !== null ? value.max : range.max
            ];
        },
        // GRID VIEW
        isInCart() {
            return itemId => this.$store.getters["eCommerce/isInCart"](itemId);
        },
        isInWishList() {
            return itemId => this.$store.getters["eCommerce/isInWishList"](itemId);
        },
        windowWidth() {
            return this.$store.state.windowWidth;
        }
    },
    watch: {
        windowWidth() {
            this.setSidebarWidth();
        },
        itemsList(data, dataold) {
            if (JSON.stringify(data) === JSON.stringify(dataold) || data.length == 0) {
                this.$nextTick(function () {
                    this.$vs.loading.close();
                });
            }
        },
        categorie() {
            this.pageActuel = 1
            this.$vs.loading({
                background: this.backgroundLoading,
                type: 'point',
                scale: 0.45,
                clickEffect: true
            });
            this.filtrer();
        },
        prix() {
            this.pageActuel = 1
            this.$vs.loading({
                background: this.backgroundLoading,
                type: 'point',
                scale: 0.45,
                clickEffect: true
            });
            this.filtrer();
        }
    },
    methods: {
        changeShopCart(action, code, designation) {
            if (action == 'add') {
                const panierObject = {
                    id: this.$store.state.AppActiveUser.id,
                    code: code,
                    designation: designation,
                    qte: 1
                }
                console.log(panierObject);
                indexPanier.add(panierObject).then(response => {
                    console.log(response)
                }).catch(error => {
                    this.$vs.notify({
                        title: "Erreur s'est produite lors de la récupération des données",
                        text: error.data,
                        color: "danger",
                        iconPack: "feather",
                        icon: "icon-alert-circles"
                    });
                })
            }
        },
        filtrer() {
            var FiltredList = this.$store.state.article.articles;
            if (this.categorie != "All") {
                var categorieFilter = this.categorie;
                FiltredList = FiltredList.filter(function (element) {
                    return element.categorie == categorieFilter;
                })
            }
            var prixMin = this.prix[0];
            var prixMax = this.prix[1];
            if (prixMin != 0 || prixMax != 1000) {
                FiltredList = FiltredList.filter(function (element) {
                    return element.prix >= prixMin &&
                        element.prix <= prixMax;
                })
            }
            this.itemsData = FiltredList;
            this.itemsList = FiltredList.slice(0, 20)
            this.nbPages = Math.floor((FiltredList.length) / 20);
        },
        rechercher(e) {
            this.filtrer();
            if (e != '') {
                this.itemsData = this.itemsData.filter(function (elem) {
                    return elem.search_Col.toLowerCase().indexOf(e) > -1;
                });
                this.itemsList = this.itemsData.slice(0, 20)
                this.nbPages = Math.floor((this.itemsData.length) / 20);
            }
        },
        paginate(val) {
            this.$vs.loading({
                background: this.backgroundLoading,
                type: 'point',
                scale: 0.45,
                clickEffect: true
            });
            window.scrollTo(0, 0);
            this.itemsList = this.itemsData.slice(val, val + 20)
        },
        items() {
            return this.$store.state.eCommerce.wishList;
        },
        setSidebarWidth() {
            if (this.windowWidth < 992) {
                this.isFilterSidebarActive = this.clickNotClose = false;
            } else {
                this.isFilterSidebarActive = this.clickNotClose = true;
            }
        },
        // GRID VIEW - ACTIONS
        toggleFilterSidebar() {
            if (this.clickNotClose) return;
            this.isFilterSidebarActive = !this.isFilterSidebarActive;
        },
        toggleItemInWishList(item) {
            this.$store.dispatch("eCommerce/toggleItemInWishList", item);
        },
        additemInCart(item) {
            this.$store.dispatch("eCommerce/additemInCart", item);
        },
        cartButtonClicked(item) {
            this.isInCart(item.objectID) ?
                this.$router.push("/apps/eCommerce/checkout").catch(() => {}) :
                this.additemInCart(item);
        }
    },
    created() {
        this.$store.commit("UPDATE_MAIN_LAYOUT_TYPE", 'Horizontal')
        if (this.$store.state.article.articles.length != 0) {
            this.$vs.loading({
                background: this.backgroundLoading,
                type: 'point',
                scale: 0.45,
                clickEffect: true
            });
            this.itemsData = this.$store.state.article.articles;
            this.itemsList = this.$store.state.article.articles.slice(0, 20).reverse();
            this.nbPages = Math.floor((this.$store.state.article.articles.length) / 20)
        }
        this.$store.subscribe(mutation => {
            if (mutation.type === "article/UPDATE_ARTICLE") {
                this.$vs.loading({
                    background: this.backgroundLoading,
                    type: 'point',
                    scale: 0.45,
                    clickEffect: true
                });
                this.itemsData = this.$store.state.article.articles;
                this.itemsList = this.$store.state.article.articles.slice(0, 20).reverse();
                this.nbPages = Math.floor((this.$store.state.article.articles.length) / 20)
            }
        });
        this.setSidebarWidth();
    },
    destroyed() {
        if (this.$router.app._route.name != 'item-detail-view') {
            this.$store.commit("UPDATE_MAIN_LAYOUT_TYPE", 'vertical')
            this.$store.dispatch('TOGGLEREDUCEBUTTON', false);
        }
    }
};
</script>

<style lang="scss">
#algolia-instant-search-demo {
    .algolia-header {
        .algolia-filters-label {
            width: calc(260px + 2.4rem);
        }
    }

    #algolia-content-container {
        .vs-sidebar {
            position: relative;
            float: left;
        }
    }

    .algolia-search-input-right-aligned-icon {
        padding: 1rem 1.5rem;
    }

    .algolia-price-slider {
        min-width: unset;
    }

    .item-view-primary-action-btn {
        color: #2c2c2c !important;
        background-color: #f6f6f6;
        min-width: 50%;
    }

    .item-view-secondary-action-btn {
        min-width: 50%;
    }
}

.theme-dark {
    #algolia-instant-search-demo {
        #algolia-content-container {
            .vs-sidebar {
                background-color: #10163a;
            }
        }
    }
}

@media (min-width: 992px) {
    .vs-sidebar-rounded {
        .vs-sidebar {
            border-radius: 0.5rem;
        }

        .vs-sidebar--items {
            border-radius: 0.5rem;
        }
    }
}

@media (max-width: 992px) {
    #algolia-content-container {
        .vs-sidebar {
            position: absolute !important;
            float: none !important;
        }
    }
}
</style>
